import { ca } from '@cian/analytics';
import { useDeviceType } from '@cian/ui-kit/responsive';
import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TCoordinate } from '../../../../../packages/api-models/common/json_query';
import { fetchHybridSearchData } from '../../../../common/actions/fetchHybridSearchData';
import { addGeo } from '../../../../common/actions/jsonQuery';
import { preparePolygons } from '../../../../common/mappers/polygons';
import { IPolygon } from '../../../../common/types/map/polygon';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { setAwaitingGeoFocus } from '../../../actions/geoFocus';
import { selectIsDrawingMode } from '../../../selectors';

interface IUseMapPolygonsParams {
  ymaps: YMaps.IYMaps;
  map: YMaps.Map;
}

const DEFAULT_POLYGON_OPTIONS: YMaps.IPolygonOptions = {
  fillColor: 'rgba(35, 133, 222, 0.1)',
  strokeColor: '#2385de',
  interactivityModel: 'default#transparent',
  strokeWidth: 3,
};

function createPolygon(ymaps: YMaps.IYMaps, coords: YMaps.TCoord[]): YMaps.Polygon {
  return new ymaps.Polygon([coords], {}, DEFAULT_POLYGON_OPTIONS);
}

function trackAreaSelect(label: 'start' | 'success' | 'close'): void {
  ca('eventSite', {
    name: 'oldevent',
    action: 'select_area',
    category: 'Mob_map',
    label,
  });
}

export function usePolygons({ ymaps, map }: IUseMapPolygonsParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const isDrawingMode = useSelector(selectIsDrawingMode);
  const polygons: IPolygon[] = useSelector((state: IApplicationState) =>
    preparePolygons(state.filters.jsonQuery).map(polygon => ({
      ...polygon,
      coordinates: polygon.coordinates.map(([a, b]) => [b, a]),
    })),
  );

  const drawPolygonBehaviorRef = React.useRef<YMaps.DrawPolygon | null>(null);
  const geoObjectsRef = React.useRef<{ [id: string]: null | YMaps.IGeoObject | YMaps.Polygon }>({});

  const handleAddPolygon = React.useCallback(
    (coordinates: [number, number][]) => {
      const preparedCoordinates: TCoordinate[] = coordinates.map(
        ([a, b]) =>
          [parseFloat(Number(b).toFixed(7)).toString(), parseFloat(Number(a).toFixed(7)).toString()] as TCoordinate,
      );

      preparedCoordinates.push(preparedCoordinates[0]);

      dispatch(
        addGeo({
          type: 'polygon',
          name: 'Область поиска',
          coordinates: preparedCoordinates,
        }),
      );
      dispatch(setAwaitingGeoFocus(true));
      dispatch(fetchHybridSearchData({ updateBounds: true, deviceType }));

      if (drawPolygonBehaviorRef.current) {
        drawPolygonBehaviorRef.current.disable();
      }
      trackAreaSelect('success');
    },
    [deviceType, dispatch],
  );

  React.useEffect(() => {
    drawPolygonBehaviorRef.current = new ymaps.cian.DrawPolygon({
      map,
      onCreatePolygon: handleAddPolygon,
    });

    return () => {
      if (drawPolygonBehaviorRef.current) {
        drawPolygonBehaviorRef.current.destroy();
      }

      const ids = Object.keys(geoObjectsRef.current);
      ids.forEach(id => {
        const geoObject = geoObjectsRef.current[id];
        if (geoObject) {
          map.geoObjects.remove(geoObject);
          delete geoObjectsRef.current[id];
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!drawPolygonBehaviorRef.current) {
      return;
    }

    if (isDrawingMode) {
      drawPolygonBehaviorRef.current.enable();
    } else {
      drawPolygonBehaviorRef.current.disable();
    }
  }, [isDrawingMode]);

  React.useEffect(() => {
    const aliveKeys = Object.keys(geoObjectsRef.current);
    const actualKeys = polygons.map(({ id }) => id);
    const toRemove = R.difference(aliveKeys, actualKeys);

    toRemove.forEach(id => {
      const geoObject = geoObjectsRef.current[id];
      if (geoObject) {
        map.geoObjects.remove(geoObject);
        delete geoObjectsRef.current[id];
      }
    });

    polygons.forEach((polygon: IPolygon) => {
      const { id } = polygon;
      if (!geoObjectsRef.current[id]) {
        const geoObject = createPolygon(ymaps, polygon.coordinates);
        map.geoObjects.add(geoObject);
        geoObjectsRef.current[id] = geoObject;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygons]);
}
